import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { FormikHelpers, FormikProps } from 'formik';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { usePensionEnrollmentInfo } from './hooks/usePensionEnrollmentInfo';
import { usePensionEnrollmentSettings } from './hooks/usePensionEnrollmentSettings';
import { PensionEnrollmentFormValues } from './models/pension-enrollment-form-model';
import { PensionEnrollmentState, PensionEnrollmentStep } from './models/pension-enrollment-model';
import { PensionEnrollmentForm } from './components/pension-enrollment-form';
import { clearPensionInfo, setPensionEnrollmentStep, setSuccessfulEnrolledPlan } from './pension-enrollment-slice';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { enrollPensionPlan } from './pension-enrollment-requests';
import { EmploymentState } from '../employment/employment-model';
import { MappedMessage } from '../../../../global/components/messages/mapped-message-display';
import { DemographicsContext } from '../../../../global/models/demographics-model';
import { Person } from '../../../../global/models/person-model';
import { MaritalStatuses } from '../../../../global/constants/lists';
import { useParentRoute } from '../../../../global/hooks/useParentRoute';
import { AccountInfoRoutes } from '../../../account-info/constants/routes';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { useEmploymentInfo } from '../employment/useEmployment';
import { useLockedPageState } from '../../../../global/hooks/useLockedPageState';
import { LockedPageKeys } from '../../../../global/models/locked-page-model';
import { LockedPageMessage } from '../../../../global/components/locked-page-message';
import { WysiwygWithReactLink } from '../../../../global/components/wysiwyg-with-react-link';
import { ProcessName, RegisterStartProcess, RegisterCompletedProcess } from '../../../../global/helpers/analytics-helper';
import { setNavigationContext } from '../../../../global/ducks/app-context';
import { NavigationContext } from '../../../../global/models/app-context-model';

interface Props {
	settingsId: string;
}

export function PensionEnrollmentApp(props: Props) {
	const { settingsId } = props;

	const app = useContext(DemographicsContext);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const pensionEnrollmentProps = epiProps?.pensionEnrollmentSettingsState?.epiContent;
	const labelSettings = epiProps?.labelSettingState?.epiContent;
	const pensionEnrollmentState = useSelector<TcsmStates, PensionEnrollmentState>(state => state.pensionEnrollmentState);
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state.employmentState);
	const person = useSelector<TcsmStates, Person>(state => state.personalInfo[app]?.currentPerson);
	const settingsLoaded = usePensionEnrollmentSettings(settingsId);
	const pensionInfoLoaded = usePensionEnrollmentInfo();
	const employmentLoaded = useEmploymentInfo();
	const dispatch = useDispatch();
	const history = useHistory();
	const parentRoute = useParentRoute();
	const maritalStatusLink = `${parentRoute}${AccountInfoRoutes.RelationshipsSegment}${AccountInfoRoutes.MaritalStatusSegment}`;
	const mapHome = useBasePath(ArcRoutes.ArcRoot);

	const currentStep = pensionEnrollmentState?.currentStep;
	const plansAvailable = pensionEnrollmentState?.pensionInfo?.plans?.some(plan => (plan.eligibility_result?.errors?.length || 0) < 1);

	const lockedState = useLockedPageState(LockedPageKeys.Enrollments);

    RegisterStartProcess(ProcessName.Pension);

    useEffect(() => {
        dispatch(setPensionEnrollmentStep(PensionEnrollmentStep.Landing));
        dispatch(setSuccessfulEnrolledPlan(''));
		dispatch(setNavigationContext(NavigationContext.Institution));

		// clear info when app unmounts
		return function cleanup() {
			dispatch(apiErrorClear());
			dispatch(clearPensionInfo());
		};
	}, []);

	useEffect(() => {
		if (pensionInfoLoaded) {
			// if person is single and no enrolled plans, set step to verify marital status
			if (plansAvailable
				&& (!person?.marital_status || person?.marital_status === MaritalStatuses.Single)
				&& (pensionEnrollmentState?.pensionInfo?.enrolled_plans?.length || 0) < 1) {
				dispatch(setPensionEnrollmentStep(PensionEnrollmentStep.Marital));
			}
		}
	}, [pensionInfoLoaded]);

	useEffect(() => {
		if (currentStep){
			window.scrollTo(0, 0);
		}
	}, [currentStep]);


	if (!settingsLoaded || !pensionInfoLoaded || !employmentLoaded) {
		return <LoadingCircle />;
	}

	if (employmentState?.currentEmployee?.employment_id === null) {
		return <WysiwygWithReactLink content={pensionEnrollmentProps?.noEmploymentContent} />;
	}

	if (!pensionEnrollmentState?.pensionInfo) {
		return <WysiwygWithReactLink content={pensionEnrollmentProps?.noPensionInfoContent} />;
	}

	const cancelAction = (formikProps: FormikProps<PensionEnrollmentFormValues>) => {

		if (currentStep === PensionEnrollmentStep.Marital) {
			history.push(maritalStatusLink);
			return;
		}

		formikProps.setTouched({});
		formikProps.setErrors({});

		if (currentStep === PensionEnrollmentStep.Landing) {
			formikProps.resetForm();
		} else if (currentStep === PensionEnrollmentStep.Detail) {
			dispatch(setPensionEnrollmentStep(PensionEnrollmentStep.Landing));
		}
	};

	const onSubmit = (values: PensionEnrollmentFormValues, formikHelpers: FormikHelpers<PensionEnrollmentFormValues>) => {
		if (!plansAvailable) {
			history.push(mapHome);
			return;
		}

		if (currentStep === PensionEnrollmentStep.Marital) {
			dispatch(setPensionEnrollmentStep(PensionEnrollmentStep.Landing));
		} else if (currentStep === PensionEnrollmentStep.Landing) {
			dispatch(apiErrorClear());
			formikHelpers.setTouched({});
			formikHelpers.setSubmitting(false);
			dispatch(setPensionEnrollmentStep(PensionEnrollmentStep.Detail));
		} else if (currentStep === PensionEnrollmentStep.Detail) {
			const selectedPlan = pensionEnrollmentState?.pensionInfo?.plans?.find(plan => plan.pbpor_id === values.plan);
			const onSuccess = () => {
				formikHelpers?.resetForm();
				RegisterCompletedProcess(ProcessName.Pension);
			};
			enrollPensionPlan(dispatch,
				values,
				selectedPlan,
				employmentState?.currentEmployee?.guid,
				employmentState?.currentEmployee?.employment_id,
				onSuccess,
			);

		}
	};

	const renderPageContent = () => {
		if (lockedState.isLocked) {
			return (
				<LockedPageMessage lockedState={lockedState} />
			);
		}

		return (
			<PensionEnrollmentForm
				currentStep={currentStep}
				pensionInfo={pensionEnrollmentState?.pensionInfo}
				pensionEnrollmentProps={pensionEnrollmentProps}
				labelSettings={labelSettings}
				cancelAction={cancelAction}
				onSubmit={onSubmit}
				plansAvailable={plansAvailable}
			/>

		);
	};

    return (
        <>
            {
                currentStep === PensionEnrollmentStep.Landing && pensionEnrollmentState?.successfulEnrolledPlan &&
                <MappedMessage
                    message={pensionEnrollmentProps?.enrollmentSuccessMessageHtml}
                    supplement={<b>{pensionEnrollmentState?.successfulEnrolledPlan}</b>} />
            }

			<PageHeader
				headerText={currentStep === PensionEnrollmentStep.Detail
					? pensionEnrollmentProps?.enrollmentDetailsHeader
					: pensionEnrollmentProps?.pensionPlansHeader}
			/>

			{renderPageContent()}
		</>
	);
}